/**
 * Applies free-text sanitizers to {@link ClaimFlowDraft} string fields before sessionStorage writes.
 * @module claims/sanitizeClaimDraftFreeText
 */

import type { ClaimFlowDraft } from './claimDraftStorage'
import { sanitizeRegistrationNumber } from '../utils/validation'
import {
  CLAIM_ATTACHMENT_FILE_NAME_MAX_LEN,
  CLAIM_COLLISION_OTHER_MAX_LEN,
  CLAIM_NARRATIVE_MAX_LEN,
  CLAIM_OTHER_PARTY_ADDRESS_MAX_LEN,
  INCIDENT_STREET_MAX_LEN,
  INCIDENT_SUBURB_MAX_LEN,
  sanitizeClaimAttachmentFileName,
  sanitizeClaimEmailContact,
  sanitizeClaimIncidentRoughLocation,
  sanitizeClaimIncidentSingleLine,
  sanitizeClaimLicenseNumber,
  sanitizeClaimMultilineNarrative,
  sanitizeClaimPersonName,
  sanitizeClaimPhoneContact,
  sanitizeClaimRepairerCompany,
  sanitizeClaimSingleLineField,
} from '../utils/claimFormTextSanitize'

const MULTILINE_NARRATIVE_KEYS = [
  'windscreenDescription',
  'multiVehicleClaimDescription',
  'weatherClaimConditionsDescription',
  'fireClaimTypeDescription',
  'theftOrBreakInDetailDescription',
  'multiVehicleIncidentDescription',
  'multiVehicleDamageDescription',
  'singleVehicleIncidentDescription',
  'singleVehicleDamageDescription',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

const PERSON_NAME_KEYS = [
  'multiVehicleDriverFirstName',
  'multiVehicleDriverLastName',
  'multiVehicleOtherPartyName',
  'multiVehicleOtherParty2Name',
  'multiVehicleOtherParty3Name',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

const LICENSE_KEYS = [
  'multiVehicleLicenseNumber',
  'multiVehicleOtherPartyLicenceNumber',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

const OTHER_PARTY_REGISTRATION_KEYS = [
  'multiVehicleOtherPartyRegistration',
  'multiVehicleOtherParty2Registration',
  'multiVehicleOtherParty3Registration',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

const PHONE_KEYS = [
  'multiVehicleOtherPartyPhone',
  'multiVehicleOtherParty2Phone',
  'multiVehicleOtherParty3Phone',
  'windscreenRepairerPhone',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

const EMAIL_KEYS = ['windscreenRepairerEmail'] as const satisfies readonly (keyof ClaimFlowDraft)[]

const REPAIRER_COMPANY_KEYS = ['windscreenRepairerCompany'] as const satisfies readonly (keyof ClaimFlowDraft)[]

const DOB_DD_MM_KEYS = [
  'multiVehicleDriverDobDdMmYyyy',
  'multiVehicleLicenseExpiryDdMmYyyy',
  'multiVehicleOtherPartyDobDdMmYyyy',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

type DraftRecord = Record<string, unknown>

/**
 * Sanitizes known free-text keys on a draft or persist patch before writing to sessionStorage.
 */
export function sanitizeClaimDraftFreeTextFields(
  draft: ClaimFlowDraft | DraftRecord
): ClaimFlowDraft | DraftRecord {
  const out: DraftRecord = { ...draft }

  for (const key of MULTILINE_NARRATIVE_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimMultilineNarrative(v, CLAIM_NARRATIVE_MAX_LEN)
    }
  }

  for (const key of PERSON_NAME_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimPersonName(v)
    }
  }

  for (const key of LICENSE_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimLicenseNumber(v)
    }
  }

  for (const key of OTHER_PARTY_REGISTRATION_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      const sanitized = sanitizeRegistrationNumber(v)
      out[key] = sanitized || null
    }
  }

  for (const key of PHONE_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimPhoneContact(v)
    }
  }

  for (const key of EMAIL_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimEmailContact(v)
    }
  }

  for (const key of REPAIRER_COMPANY_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimRepairerCompany(v)
    }
  }

  for (const key of DOB_DD_MM_KEYS) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimSingleLineField(v, 10)
    }
  }

  if (typeof out.singleVehicleCollisionTargetOtherText === 'string') {
    out.singleVehicleCollisionTargetOtherText = sanitizeClaimMultilineNarrative(
      out.singleVehicleCollisionTargetOtherText,
      CLAIM_COLLISION_OTHER_MAX_LEN
    )
  }

  if (typeof out.incidentAddressSearch === 'string') {
    out.incidentAddressSearch = sanitizeClaimIncidentRoughLocation(out.incidentAddressSearch)
  }
  if (typeof out.incidentStreet === 'string') {
    out.incidentStreet = sanitizeClaimIncidentSingleLine(out.incidentStreet, INCIDENT_STREET_MAX_LEN)
  }
  if (typeof out.incidentSuburb === 'string') {
    out.incidentSuburb = sanitizeClaimIncidentSingleLine(out.incidentSuburb, INCIDENT_SUBURB_MAX_LEN)
  }
  if (typeof out.incidentPostcode === 'string') {
    out.incidentPostcode = sanitizeClaimSingleLineField(out.incidentPostcode, 10)
  }
  if (typeof out.incidentState === 'string') {
    out.incidentState = sanitizeClaimSingleLineField(out.incidentState, 40)
  }

  for (const key of [
    'multiVehicleOtherPartyAddress',
    'multiVehicleOtherParty2Address',
    'multiVehicleOtherParty3Address',
  ] as const) {
    const v = out[key]
    if (typeof v === 'string') {
      out[key] = sanitizeClaimMultilineNarrative(v, CLAIM_OTHER_PARTY_ADDRESS_MAX_LEN)
    }
  }

  if (Array.isArray(out.windscreenAttachmentFileNames)) {
    out.windscreenAttachmentFileNames = out.windscreenAttachmentFileNames
      .slice(0, 20)
      .map((name) =>
        typeof name === 'string'
          ? sanitizeClaimAttachmentFileName(name, CLAIM_ATTACHMENT_FILE_NAME_MAX_LEN)
          : name
      )
  }

  return out
}
